/**
 * Legislation list store (screenshots/25, 31–34). Hydrated from `snapshot:legislation` before the first
 * render (main.tsx), so every list tab paints instantly; hovering a tab can refresh it from
 * GET /api/dashboard/legislation (prefetch, L12).
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { ListData } from "./types";

export type ListTab = "vote" | "following" | "rollcalls" | "filed" | "accuracy";
export const LIST_TABS: ListTab[] = ["vote", "following", "rollcalls", "filed", "accuracy"];

interface ListState {
  data: ListData | null;
  hydrate: (d: ListData) => void;
  /** L12: refresh in the background; the tab already has data, so nothing waits on this. */
  prefetch: () => void;
  /** Following › "Mark all seen": the "Since your last visit" lines go now and stay gone (saved on the server). */
  markSeen: () => Promise<boolean>;
}

let inflight: Promise<void> | null = null;
let seenAt = 0;   // when "Mark all seen" was pressed here: a refresh that started earlier mustn't bring the lines back

export const listStore = createStore<ListState>()((set, get) => ({
  data: null,
  hydrate: (data) => set({ data }),
  prefetch: () => {
    const started = Date.now();
    inflight ??= fetch("/api/dashboard/legislation", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: ListData | null) => {
        if (!d?.agenda) return;
        set({ data: started < seenAt ? { ...d, following: { ...d.following, changes: [] } } : d });
      })
      .catch(() => {})
      .finally(() => { setTimeout(() => { inflight = null; }, 60_000); });
  },
  markSeen: async () => {
    const before = get().data;
    if (!before?.following.changes.length) return true;
    seenAt = Date.now();
    set({ data: { ...before, following: { ...before.following, changes: [] } } });
    const ok = await fetch("/api/dashboard/legislation/seen", { method: "POST", credentials: "include" }).then((r) => r.ok, () => false);
    if (!ok) {   // not saved: show the lines again so they aren't lost
      seenAt = 0;
      const now = get().data;
      if (now) set({ data: { ...now, following: { ...now.following, changes: before.following.changes } } });
    }
    return ok;
  },
}));

export const useList = <U,>(sel: (s: ListState) => U) => useStore(listStore, sel);
